/**
 * Cross-window communication utility.
 * Uses BroadcastChannel when available with a localStorage fallback.
 */

class CrossWindowSync {
  constructor() {
    this.channel = null;
    this.listeners = new Map();
    this.windowId = this.generateWindowId();
    this.isDev = process.env.NODE_ENV === 'development';

    try {
      sessionStorage.setItem('window-id', this.windowId);
    } catch (error) {
      // Non-blocking.
    }

    this.initializeCommunication();
  }

  generateWindowId() {
    return `window-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  initializeCommunication() {
    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel('auth-sync-channel');
      this.channel.addEventListener('message', (event) => {
        this.handleMessage(event.data);
      });
    }

    window.addEventListener('storage', (event) => {
      if (event.key !== 'cross-window-message' || !event.newValue) return;
      try {
        const message = JSON.parse(event.newValue);
        this.handleMessage(message);
      } catch (error) {
        // Ignore malformed messages.
      }
    });

    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  buildMessage(message) {
    return {
      ...message,
      windowId: message.windowId || this.windowId,
      timestamp: message.timestamp || Date.now()
    };
  }

  handleMessage(message) {
    if (!message || !message.type) return;
    if (message.windowId === this.windowId) return;

    const listeners = this.listeners.get(message.type) || [];
    listeners.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        console.error('[CrossWindowSync] Listener error:', error);
      }
    });
  }

  broadcastMessage(message) {
    const payload = this.buildMessage(message);

    try {
      if (this.channel) {
        this.channel.postMessage(payload);
      } else {
        localStorage.setItem('cross-window-message', JSON.stringify(payload));
        setTimeout(() => {
          localStorage.removeItem('cross-window-message');
        }, 100);
      }
    } catch (error) {
      console.error('[CrossWindowSync] Error broadcasting message:', error);
    }
  }

  subscribe(messageType, callback) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, []);
    }
    this.listeners.get(messageType).push(callback);

    return () => {
      const listeners = this.listeners.get(messageType) || [];
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }

  broadcastAuthChange(type, authData = {}) {
    this.broadcastMessage({
      type,
      user: authData.user || null,
      profile: authData.profile || null,
      subscription: authData.subscription || null,
      remainingPrompts: authData.remainingPrompts || 0
    });
  }

  requestAuthState() {
    this.broadcastMessage({ type: 'auth-state-request' });
  }

  respondAuthState(authData = {}) {
    this.broadcastMessage({
      type: 'auth-state-response',
      user: authData.user || null,
      profile: authData.profile || null,
      subscription: authData.subscription || null,
      remainingPrompts: authData.remainingPrompts || 0
    });
  }

  getWindowId() {
    return this.windowId;
  }

  cleanup() {
    if (this.channel) {
      this.channel.close();
    }
  }
}

const crossWindowSync = new CrossWindowSync();

export default crossWindowSync;

// Test utilities for development
export const testCrossWindowSync = {
  simulateLogin: (userId = 'test-user-123') => {
    crossWindowSync.broadcastAuthChange('login', {
      user: { id: userId, email: 'test@example.com' },
      profile: { id: userId, subscription_type: 'pro' },
      subscription: { status: 'active', remaining_prompts: 100 },
      remainingPrompts: 100
    });
  },

  simulateLogout: () => {
    crossWindowSync.broadcastAuthChange('logout');
  },

  getWindowInfo: () => ({
    windowId: crossWindowSync.getWindowId()
  }),

  testMessage: (message = 'Hello from another window!') => {
    crossWindowSync.broadcastMessage({
      type: 'test-message',
      message
    });
  }
};

if (process.env.NODE_ENV === 'development') {
  window.testCrossWindowSync = testCrossWindowSync;
  window.crossWindowSync = crossWindowSync;
  console.log('[CrossWindowSync] Test utilities available at window.testCrossWindowSync');
}
